import type { RankedPartner } from './partnerRanking';

/**
 * Splits the alternatives into the one that earns a featured spot under the current partner,
 * and the rest that go into the "Andere aanbieders" list.
 *
 * Two things have to be true before a partner is featured:
 *
 * 1. We can write the header that introduces it ("We found an alternative provider {km}km from
 *    {city}") — without a location that sentence is hidden, and the card was left sitting bare
 *    beneath the current partner, reading as a second, unexplained provider.
 * 2. It is actually **nearer than the one the user is already looking at**. Being the closest of
 *    the others is not the same as being worth a look: on Highland Games from Kalmthout the
 *    current partner is 31km and €38,50, and this slot announced a "found" alternative 95km away
 *    at €110 — farther and dearer, presented as a discovery.
 *
 * Returning both halves from one place is what keeps the featured partner from also appearing
 * in the list below it.
 */
export function splitFeaturedAlternative(args: {
    alternatives: ReadonlyArray<RankedPartner>;
    /** The location the user searched from, or null/empty when they have not entered one. */
    locationQuery: string | null | undefined;
    /** Distance to the partner currently shown above this slot, when known. */
    currentDistanceKm: number | null | undefined;
}): { featured: RankedPartner | null; listed: ReadonlyArray<RankedPartner> } {
    const first = args.alternatives[0];
    if (!args.locationQuery || first?.distanceKm == null) {
        return { featured: null, listed: args.alternatives };
    }

    // No distance for the current partner means no comparison to make, so nothing is "better".
    const isNearer = args.currentDistanceKm != null && first.distanceKm < args.currentDistanceKm;
    if (!isNearer) return { featured: null, listed: args.alternatives };

    return { featured: first, listed: args.alternatives.slice(1) };
}
